// Monthly note drawer (prototype's SbNoteDrawer): free text +
// provisional « à valoir » amount + quick tags, with a tab per month
// of the current quarter. Saves are debounced 400 ms per field-set.

import { useEffect, useMemo, useRef, useState } from 'react';
import { PAYROLL_TAGS, type PayrollEmployeeDTO, type PayrollMonthDTO, type PayrollTag } from '@pointage/shared';
import { usePatchPayrollNote } from '../../api/hooks.js';
import { fmtDA } from '../../lib/format.js';
import { entityShort, monthLabel, sbAvColor, sbInitials, roleLabel } from './common.js';
import { useAutosaveFlush } from '../../lib/unsaved.js';

interface Draft {
  text: string;
  amount: string; // digits-only string while editing
  tags: PayrollTag[];
}

function draftFrom(emp: PayrollEmployeeDTO, m: number): Draft {
  const n = emp.notes[m];
  return {
    text: n?.text ?? '',
    amount: n?.amount != null ? String(n.amount) : '',
    tags: n?.tags ?? [],
  };
}

export function NoteDrawer({
  data,
  emp,
  month,
  canEdit,
  onClose,
}: {
  data: PayrollMonthDTO;
  emp: PayrollEmployeeDTO;
  month: number;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [m, setM] = useState(month);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(emp, month));
  const patchNote = usePatchPayrollNote();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraft(draftFrom(emp, m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m, emp.id]);

  // Debounced save of the whole draft (last-write-wins, 2 users max).
  const pending = useRef<(() => void) | null>(null);
  const save = (d: Draft) => {
    setDraft(d);
    if (timer.current) clearTimeout(timer.current);
    const run = () => {
      pending.current = null;
      timer.current = null;
      patchNote.mutate({
        year: data.year,
        month: m,
        employeeId: emp.id,
        text: d.text,
        amount: d.amount === '' ? null : Number(d.amount),
        tags: d.tags,
      });
    };
    pending.current = run;
    timer.current = setTimeout(run, 400);
  };
  // Le délai en attente part immédiatement quand on ferme le tiroir ou
  // qu'on quitte l'écran — plus de note perdue dans la fenêtre de 400 ms.
  useAutosaveFlush(`payroll-note-${emp.id}`, () => {
    if (timer.current) clearTimeout(timer.current);
    pending.current?.();
  });

  // Live suggested total: server values for other months + this draft.
  const suggested = useMemo(() => {
    return data.quarterMonths.reduce((a, qm) => {
      if (qm === m) return a + (draft.amount === '' ? 0 : Number(draft.amount));
      return a + (emp.notes[qm]?.amount ?? 0);
    }, 0);
  }, [data.quarterMonths, draft.amount, emp.notes, m]);

  const hasNote = (qm: number) => {
    const n = emp.notes[qm];
    return !!n && (n.text !== '' || n.amount != null || n.tags.length > 0);
  };

  return (
    <div className="sb-drawer-veil" onClick={onClose}>
      <aside className="sb-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="sb-dr-head">
          <span
            className="gm-av"
            style={{ background: sbAvColor(emp.id), width: 40, height: 40, borderRadius: 11, fontSize: 13 }}
          >
            {sbInitials(emp.name)}
          </span>
          <div>
            <div className="sb-dr-name">{emp.name}</div>
            <div className="sb-dr-role">{roleLabel(emp)} · {entityShort(emp.entityId)}</div>
          </div>
          <button type="button" className="sb-dr-x" onClick={onClose}>✕</button>
        </div>
        <div className="sb-dr-tabs">
          {data.quarterMonths.map((qm) => (
            <button key={qm} type="button" className={'sb-dr-tab' + (m === qm ? ' on' : '')} onClick={() => setM(qm)}>
              {monthLabel(qm)}
              {hasNote(qm) ? <i className="sb-dr-dot" /> : null}
            </button>
          ))}
        </div>
        <label className="sb-dr-lbl">Note du mois</label>
        <textarea
          className="sb-dr-ta"
          rows={4}
          disabled={!canEdit}
          placeholder={canEdit ? 'Perf, incidents, promesses… à retrouver en fin de trimestre' : '—'}
          value={draft.text}
          onChange={(e) => save({ ...draft, text: e.target.value })}
        />
        <label className="sb-dr-lbl">Montant provisoire (« à valoir » sur le bonus T{data.quarter})</label>
        <div className="sb-dr-amt">
          <input
            className="sb-dr-in gm-mono"
            type="text"
            inputMode="numeric"
            disabled={!canEdit}
            placeholder="0"
            value={draft.amount}
            onChange={(e) => save({ ...draft, amount: e.target.value.replace(/[^\d]/g, '') })}
          />
          <span className="sb-dr-da">DA</span>
        </div>
        <label className="sb-dr-lbl">Tags rapides</label>
        <div className="sb-dr-tags">
          {(Object.entries(PAYROLL_TAGS) as Array<[PayrollTag, { label: string }]>).map(([k, tg]) => {
            const on = draft.tags.includes(k);
            return (
              <button
                key={k}
                type="button"
                className={'sb-tag' + (on ? ' on' : '')}
                disabled={!canEdit}
                style={on ? { background: tagBg(k), color: tagColor(k), borderColor: 'transparent' } : {}}
                onClick={() => save({ ...draft, tags: on ? draft.tags.filter((x) => x !== k) : [...draft.tags, k] })}
              >
                {tg.label}
              </button>
            );
          })}
        </div>
        <div className="sb-dr-sum">
          <span>Provisoire cumulé T{data.quarter}</span>
          <b className="gm-mono">{fmtDA(suggested)} DA</b>
        </div>
        <div className="sb-dr-hint">
          Ces notes alimentent l'onglet <b>Bonus T{data.quarter}</b> : le cumul est proposé comme point de départ du bonus final.
        </div>
      </aside>
    </div>
  );
}

// Tag chip colors (island tokens — resolved by the .sbapp scope).
export function tagBg(k: PayrollTag): string {
  return { perf: 'var(--greens)', projet: 'var(--shfs)', client: 'var(--infos)', retard: 'var(--warns)', augm: 'var(--bads)' }[k];
}
export function tagColor(k: PayrollTag): string {
  return { perf: 'var(--greenc)', projet: 'var(--shf)', client: 'var(--infoc)', retard: 'var(--warnc)', augm: 'var(--badc)' }[k];
}
